import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  Card,
  Button,
  Checkbox,
  useTheme,
  ProgressBar,
  Divider,
  IconButton,
} from 'react-native-paper';
import { JourneyPreviewCard } from './JourneyPreviewCard';
import { JourneyCustomizationPanel } from './JourneyCustomizationPanel';
import type {
  JourneyAcceptanceFlow as JourneyAcceptanceFlowType,
  UserConfirmation,
  GeneratedJourney,
  JourneyCustomization,
} from './JourneyPreviewTypes';

interface JourneyAcceptanceFlowProps {
  journey: GeneratedJourney;
  customizationOptions?: any[];
  onAccept: (
    journey: GeneratedJourney,
    confirmations: UserConfirmation[]
  ) => void;
  onCancel: () => void;
  onBack?: () => void;
  isLoading?: boolean;
  style?: any;
}

export function JourneyAcceptanceFlow({
  journey,
  customizationOptions = [],
  onAccept,
  onCancel,
  onBack,
  isLoading = false,
  style,
}: JourneyAcceptanceFlowProps) {
  const theme = useTheme();
  const [flowState, setFlowState] = useState<JourneyAcceptanceFlowType>({
    step: 'preview',
    journey,
    customizations: [],
    userConfirmations: [],
  });

  const [confirmations, setConfirmations] = useState<UserConfirmation[]>([]);

  // Initialize confirmations based on journey
  useEffect(() => {
    const initialConfirmations: UserConfirmation[] = [
      {
        type: 'time_commitment',
        question: `I understand this journey requires ${journey.time_commitment_hours_per_week} hours per week for ${journey.duration_weeks} weeks`,
        confirmed: false,
      },
      {
        type: 'difficulty_level',
        question: `I'm comfortable with the ${journey.skill_level} difficulty level`,
        confirmed: false,
      },
      {
        type: 'content_preferences',
        question: `I'm interested in the selected focus areas: ${journey.focus_areas.slice(0, 3).join(', ')}${journey.focus_areas.length > 3 ? ` and ${journey.focus_areas.length - 3} more` : ''}`,
        confirmed: false,
      },
      {
        type: 'goals',
        question:
          'I understand this journey is personalized based on my assessment and can be adjusted as I progress',
        confirmed: false,
      },
    ];

    setConfirmations(initialConfirmations);
    setFlowState(prev => ({
      ...prev,
      userConfirmations: initialConfirmations,
    }));
  }, [journey]);

  const handleStepChange = (newStep: JourneyAcceptanceFlowType['step']) => {
    setFlowState(prev => ({
      ...prev,
      step: newStep,
    }));
  };

  const handleCustomization = (customization: JourneyCustomization) => {
    setFlowState(prev => ({
      ...prev,
      customizations: [
        ...prev.customizations.filter(
          c => c.optionId !== customization.optionId
        ),
        customization,
      ],
    }));
  };

  const handlePreviewCustomizations = (
    customizations: JourneyCustomization[]
  ) => {
    setFlowState(prev => ({
      ...prev,
      customizations,
      step: 'preview',
    }));
  };

  const handleConfirmationChange = (
    index: number,
    confirmed: boolean,
    reasoning?: string
  ) => {
    const updatedConfirmations = confirmations.map((conf, i) =>
      i === index ? { ...conf, confirmed, reasoning } : conf
    );
    setConfirmations(updatedConfirmations);
    setFlowState(prev => ({
      ...prev,
      userConfirmations: updatedConfirmations,
    }));
  };

  const handleAccept = () => {
    if (flowState.step === 'confirm') {
      onAccept(flowState.journey, confirmations);
    } else {
      handleStepChange('confirm');
    }
  };

  const getStepProgress = () => {
    const steps = ['preview', 'customize', 'confirm', 'complete'];
    const currentIndex = steps.indexOf(flowState.step);
    return (currentIndex + 1) / steps.length;
  };

  const getStepTitle = () => {
    switch (flowState.step) {
      case 'preview':
        return 'Journey Preview';
      case 'customize':
        return 'Customize Journey';
      case 'confirm':
        return 'Confirm Details';
      case 'complete':
        return 'Journey Created';
      default:
        return 'Journey Setup';
    }
  };

  const canProceed = () => {
    if (flowState.step === 'confirm') {
      return confirmations.every(conf => conf.confirmed);
    }
    return true;
  };

  const renderStepContent = () => {
    switch (flowState.step) {
      case 'preview':
        return (
          <View style={styles.stepContent}>
            <JourneyPreviewCard
              journey={flowState.journey}
              showExplanation={true}
              showCustomization={customizationOptions.length > 0}
              onCustomize={() => handleStepChange('customize')}
              onAccept={handleAccept}
              onViewDetails={() => {
                // Could open a detailed view modal
              }}
            />
          </View>
        );

      case 'customize':
        return (
          <View style={styles.stepContent}>
            <JourneyCustomizationPanel
              journey={flowState.journey}
              options={customizationOptions}
              onCustomize={handleCustomization}
              onPreview={handlePreviewCustomizations}
              isLoading={isLoading}
            />
          </View>
        );

      case 'confirm':
        return (
          <View style={styles.stepContent}>
            <ScrollView showsVerticalScrollIndicator={false}>
              {/* Journey Summary */}
              <Card style={styles.summaryCard} mode='outlined'>
                <Card.Content>
                  <Text variant='titleMedium' style={styles.summaryTitle}>
                    Journey Summary
                  </Text>
                  <View style={styles.summaryDetails}>
                    <View style={styles.summaryRow}>
                      <Text variant='bodyMedium' style={styles.summaryLabel}>
                        Title:
                      </Text>
                      <Text variant='bodyMedium' style={styles.summaryValue}>
                        {flowState.journey.title}
                      </Text>
                    </View>
                    <View style={styles.summaryRow}>
                      <Text variant='bodyMedium' style={styles.summaryLabel}>
                        Duration:
                      </Text>
                      <Text variant='bodyMedium' style={styles.summaryValue}>
                        {flowState.journey.duration_weeks} weeks
                      </Text>
                    </View>
                    <View style={styles.summaryRow}>
                      <Text variant='bodyMedium' style={styles.summaryLabel}>
                        Time Commitment:
                      </Text>
                      <Text variant='bodyMedium' style={styles.summaryValue}>
                        {flowState.journey.time_commitment_hours_per_week}h/week
                      </Text>
                    </View>
                    <View style={styles.summaryRow}>
                      <Text variant='bodyMedium' style={styles.summaryLabel}>
                        Skill Level:
                      </Text>
                      <Text variant='bodyMedium' style={styles.summaryValue}>
                        {flowState.journey.skill_level.charAt(0).toUpperCase() +
                          flowState.journey.skill_level.slice(1)}
                      </Text>
                    </View>
                  </View>

                  {/* Customizations Applied */}
                  {flowState.customizations.length > 0 && (
                    <View style={styles.customizationsSection}>
                      <Divider style={styles.divider} />
                      <Text
                        variant='titleSmall'
                        style={styles.customizationsTitle}
                      >
                        Customizations Applied
                      </Text>
                      {flowState.customizations.map((customization, index) => (
                        <Text
                          key={index}
                          variant='bodySmall'
                          style={styles.customizationItem}
                        >
                          •{' '}
                          {customization.reasoning ||
                            `Modified ${customization.optionId}`}
                        </Text>
                      ))}
                    </View>
                  )}
                </Card.Content>
              </Card>

              {/* Confirmations */}
              <Card style={styles.confirmationsCard} mode='outlined'>
                <Card.Content>
                  <Text variant='titleMedium' style={styles.confirmationsTitle}>
                    Please Confirm
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={styles.confirmationsSubtitle}
                  >
                    Please review and confirm these details before starting your
                    journey
                  </Text>

                  <View style={styles.confirmationsList}>
                    {confirmations.map((confirmation, index) => (
                      <View key={index} style={styles.confirmationItem}>
                        <Checkbox
                          status={
                            confirmation.confirmed ? 'checked' : 'unchecked'
                          }
                          onPress={() =>
                            handleConfirmationChange(
                              index,
                              !confirmation.confirmed
                            )
                          }
                        />
                        <Text
                          variant='bodyMedium'
                          style={styles.confirmationText}
                        >
                          {confirmation.question}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {!canProceed() && (
                    <Text variant='bodySmall' style={styles.confirmationHelper}>
                      💡 Please confirm all items above to start your journey
                    </Text>
                  )}
                </Card.Content>
              </Card>
            </ScrollView>
          </View>
        );

      case 'complete':
        return (
          <View style={styles.stepContent}>
            <Card style={styles.completeCard} mode='outlined'>
              <Card.Content style={styles.completeContent}>
                <Text variant='headlineMedium' style={styles.completeTitle}>
                  🎉 Journey Created!
                </Text>
                <Text variant='bodyMedium' style={styles.completeMessage}>
                  Your personalized coffee learning journey is ready to begin.
                  You can start your first activity right away or explore the
                  journey structure.
                </Text>
              </Card.Content>
            </Card>
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <View style={[styles.container, style]}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          {onBack && flowState.step !== 'preview' && (
            <IconButton
              icon='arrow-left'
              size={24}
              onPress={() => {
                if (flowState.step === 'customize') {
                  handleStepChange('preview');
                } else if (flowState.step === 'confirm') {
                  handleStepChange(
                    customizationOptions.length > 0 ? 'customize' : 'preview'
                  );
                } else {
                  onBack();
                }
              }}
            />
          )}
          <View style={styles.headerTitleContainer}>
            <Text variant='titleLarge' style={styles.headerTitle}>
              {getStepTitle()}
            </Text>
          </View>
          <IconButton icon='close' size={24} onPress={onCancel} />
        </View>

        {/* Progress Bar */}
        <View style={styles.progressContainer}>
          <ProgressBar
            progress={getStepProgress()}
            color={theme.colors.primary}
            style={styles.progressBar}
          />
          <Text variant='bodySmall' style={styles.progressText}>
            Step {Math.ceil(getStepProgress() * 4)} of 4
          </Text>
        </View>
      </View>

      {/* Step Content */}
      <View style={styles.content}>{renderStepContent()}</View>

      {/* Footer Actions */}
      {flowState.step !== 'complete' && (
        <View style={styles.footer}>
          <View style={styles.footerActions}>
            {flowState.step === 'confirm' ? (
              <Button
                mode='contained'
                onPress={handleAccept}
                disabled={!canProceed() || isLoading}
                loading={isLoading}
                style={styles.acceptButton}
                icon='rocket-launch'
              >
                Start My Journey
              </Button>
            ) : (
              <Button
                mode='contained'
                onPress={handleAccept}
                disabled={isLoading}
                style={styles.continueButton}
                icon='arrow-right'
              >
                Continue
              </Button>
            )}
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: 'white',
    paddingTop: 8,
    paddingBottom: 16,
    elevation: 2,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontWeight: '600',
  },
  progressContainer: {
    paddingHorizontal: 16,
    marginTop: 12,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    marginBottom: 8,
  },
  progressText: {
    textAlign: 'center',
    opacity: 0.7,
  },
  content: {
    flex: 1,
  },
  stepContent: {
    flex: 1,
    padding: 16,
  },
  summaryCard: {
    marginBottom: 16,
  },
  summaryTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  summaryDetails: {
    gap: 8,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontWeight: '500',
    opacity: 0.8,
  },
  summaryValue: {
    fontWeight: '600',
  },
  customizationsSection: {
    marginTop: 12,
  },
  divider: {
    marginVertical: 12,
  },
  customizationsTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  customizationItem: {
    opacity: 0.8,
    marginBottom: 4,
  },
  confirmationsCard: {
    marginBottom: 16,
  },
  confirmationsTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  confirmationsSubtitle: {
    opacity: 0.7,
    marginBottom: 16,
  },
  confirmationsList: {
    gap: 12,
  },
  confirmationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  confirmationText: {
    flex: 1,
    marginLeft: 8,
    lineHeight: 20,
  },
  confirmationHelper: {
    marginTop: 12,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  completeCard: {
    marginTop: 32,
  },
  completeContent: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  completeTitle: {
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  completeMessage: {
    textAlign: 'center',
    lineHeight: 20,
    opacity: 0.8,
  },
  footer: {
    backgroundColor: 'white',
    padding: 16,
    elevation: 2,
  },
  footerActions: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  acceptButton: {
    minWidth: 180,
  },
  continueButton: {
    minWidth: 120,
  },
});
